import { MonoTypeOperatorFunction, Observable } from 'rxjs';

/**
 * Detaches the source from the subscriber teardown: unsubscribing downstream
 * stops delivery, but never unsubscribes the source.
 *
 * `HttpClient` maps unsubscription onto `XMLHttpRequest.abort()`, so a component
 * destroyed while its own write is in flight cancels that write mid-air. The
 * browser then reports the aborted cross-origin XHR as a CORS failure, because
 * an aborted request carries no response headers left to validate — the server
 * CORS configuration is not involved.
 *
 * The creation page reproduces this by construction: a write flips a
 * `*Mutating` signal, the signal swaps the step for its page skeleton, and the
 * swap destroys the very component whose `takeUntilDestroyed` owns the
 * subscription. The write leaves and is aborted in the same change-detection
 * cycle.
 *
 * Values that arrive after the subscriber is gone are dropped, errors included —
 * the initiator no longer exists to receive them. Upstream side effects (state
 * writes, post-write rereads) still run to completion, which is what keeps the
 * `*Mutating` gates from hanging open.
 */
export function keepAlive<T>(): MonoTypeOperatorFunction<T> {
  return (source) =>
    new Observable<T>((subscriber) => {
      source.subscribe({
        next: (value) => {
          if (!subscriber.closed) subscriber.next(value);
        },
        error: (error: unknown) => {
          if (!subscriber.closed) subscriber.error(error);
        },
        complete: () => {
          if (!subscriber.closed) subscriber.complete();
        },
      });
    });
}
